import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Grid,
  Tabs,
  Tab,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  Preview as PreviewIcon,
  Send as SendIcon,
  LocationOn as LocationIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  History as HistoryIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';

const DEFAULT_RADIUS = 30;

/**
 * Admin dialog: broadcast a job notification to all active maids within a
 * radius of a given area, by email and/or SMS, plus a History view of the
 * notification trail.
 */
const MaidBroadcastDialog = ({ open, onClose }) => {
  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

  const [activeTab, setActiveTab] = useState(0);

  const [address, setAddress] = useState('');
  const [radiusMiles, setRadiusMiles] = useState(DEFAULT_RADIUS);

  const [emailEnabled, setEmailEnabled] = useState(true);
  const [smsEnabled, setSmsEnabled] = useState(false);

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [smsMessage, setSmsMessage] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [previewMeta, setPreviewMeta] = useState(null);
  const [sendResults, setSendResults] = useState(null);

  // History state
  const [campaigns, setCampaigns] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [previewCampaign, setPreviewCampaign] = useState(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  });

  const selectedChannels = [
    ...(emailEnabled ? ['email'] : []),
    ...(smsEnabled ? ['sms'] : []),
  ];

  // Recipient selection (checkbox list)
  const toggleRecipient = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const allSelected = recipients.length > 0 && recipients.every((r) => selectedIds.has(r.id));
  const someSelected = recipients.some((r) => selectedIds.has(r.id));
  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(recipients.map((r) => r.id)));
  };
  const selectedRecipients = recipients.filter((r) => selectedIds.has(r.id));
  const selectedEmailable = selectedRecipients.filter((r) => r.hasEmail).length;
  const selectedSmsable = selectedRecipients.filter((r) => r.hasPhone).length;

  const resetMessages = () => {
    setError('');
    setSuccess('');
  };

  const fetchCampaigns = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const response = await fetch(`${apiBase}/api/notifications/maids/campaigns?limit=100`, {
        headers: authHeaders(),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to load history');
      }
      setCampaigns(result.data.campaigns || []);
    } catch (err) {
      setHistoryError(err.message);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && activeTab === 1) {
      fetchCampaigns();
    }
  }, [open, activeTab, fetchCampaigns]);

  const handlePreview = async () => {
    resetMessages();
    setSendResults(null);
    if (!address.trim()) {
      setError('Enter an area address to preview recipients.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/api/notifications/maids/preview`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ address, radiusMiles: Number(radiusMiles) }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to generate preview');
      }
      const recs = result.data.recipients || [];
      setRecipients(recs);
      setSelectedIds(new Set(recs.map((r) => r.id))); // default: all selected
      setPreviewMeta(result.data);
      if (recs.length === 0) {
        setSuccess('No active technicians found within the radius.');
      } else {
        setSuccess(
          `${result.data.totalInRadius} technician(s) within ${result.data.radiusMiles} mi · ` +
            `${result.data.emailableCount} with email · ${result.data.smsableCount} with phone.`
        );
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const validateForSend = () => {
    if (!address.trim()) return 'Enter an area address.';
    if (selectedChannels.length === 0) return 'Select at least one channel (Email or SMS).';
    if (recipients.length > 0 && selectedRecipients.length === 0)
      return 'Select at least one recipient.';
    if (emailEnabled && (!subject.trim() || !message.trim()))
      return 'Subject and email message are required for the Email channel.';
    if (smsEnabled && !(smsMessage.trim() || message.trim()))
      return 'An SMS message is required for the SMS channel.';
    return null;
  };

  const handleSend = async () => {
    resetMessages();
    const validationError = validateForSend();
    if (validationError) {
      setError(validationError);
      return;
    }

    // If a preview was run, send only the checked recipients; otherwise all in radius.
    const hasSelection = recipients.length > 0;
    const counts = [];
    if (emailEnabled) counts.push(`${hasSelection ? selectedEmailable : previewMeta?.emailableCount ?? '?'} via email`);
    if (smsEnabled) counts.push(`${hasSelection ? selectedSmsable : previewMeta?.smsableCount ?? '?'} via SMS`);
    if (!window.confirm(`Send this notification (${counts.join(', ')}) within ${radiusMiles} miles?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/api/notifications/maids/send`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          address,
          radiusMiles: Number(radiusMiles),
          channels: selectedChannels,
          subject,
          message,
          smsMessage,
          ...(hasSelection ? { includeMaidIds: Array.from(selectedIds) } : {}),
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to send notifications');
      }
      setSendResults(result.data);

      const parts = [];
      if (result.data.email) {
        parts.push(`Email: ${result.data.email.totalSent} sent` +
          (result.data.email.totalFailed ? `, ${result.data.email.totalFailed} failed` : ''));
        if (result.data.email.note) parts.push(result.data.email.note);
      }
      if (result.data.sms) {
        parts.push(`SMS: ${result.data.sms.totalSent} sent` +
          (result.data.sms.totalFailed ? `, ${result.data.sms.totalFailed} failed` : ''));
        if (result.data.sms.note) parts.push(result.data.sms.note);
      }
      setSuccess(parts.join(' · '));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetMessages();
    setRecipients([]);
    setSelectedIds(new Set());
    setPreviewMeta(null);
    setSendResults(null);
    setPreviewCampaign(null);
    setActiveTab(0);
    onClose();
  };

  const channelStatusChip = (maidId, channelResult, label) => {
    if (!channelResult) return null;
    const r = channelResult.results?.find((x) => x.maidId === maidId);
    if (!r) return null;
    return (
      <Chip
        size="small"
        sx={{ ml: 0.5 }}
        label={`${label}: ${r.success ? 'sent' : 'failed'}`}
        color={r.success ? 'success' : 'error'}
        variant="outlined"
      />
    );
  };

  const renderCompose = () => (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            label="Area address"
            placeholder="e.g. 123 Main St, Orlando, FL 32801"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            helperText="The job location / center of the search radius"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Radius (miles)"
            type="number"
            value={radiusMiles}
            onChange={(e) => setRadiusMiles(e.target.value)}
            inputProps={{ min: 1, max: 200 }}
          />
        </Grid>

        <Grid item xs={12}>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={emailEnabled}
                  onChange={(e) => setEmailEnabled(e.target.checked)}
                  icon={<EmailIcon />}
                  checkedIcon={<EmailIcon />}
                />
              }
              label="Email"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={smsEnabled}
                  onChange={(e) => setSmsEnabled(e.target.checked)}
                  icon={<SmsIcon />}
                  checkedIcon={<SmsIcon />}
                />
              }
              label="SMS"
            />
          </FormGroup>
        </Grid>

        {emailEnabled && (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={5}
                label="Email message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                helperText="Use [TECHNICIAN_NAME] to insert the recipient's first name"
              />
            </Grid>
          </>
        )}

        {smsEnabled && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="SMS message"
              value={smsMessage}
              onChange={(e) => setSmsMessage(e.target.value)}
              helperText="Plain text, kept short. Falls back to the email message if left blank. Supports [TECHNICIAN_NAME]."
            />
          </Grid>
        )}
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, mt: 3, mb: 1 }}>
        <Button
          variant="outlined"
          startIcon={<PreviewIcon />}
          onClick={handlePreview}
          disabled={loading}
        >
          Preview Recipients
        </Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          onClick={handleSend}
          disabled={loading}
        >
          {loading ? 'Working...' : 'Send Notification'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ my: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ my: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {recipients.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon fontSize="small" />
            Recipients in radius — {selectedRecipients.length} of {recipients.length} selected
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onChange={toggleAll}
                      inputProps={{ 'aria-label': 'Select all recipients' }}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell align="right">Distance (mi)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recipients.map((r) => (
                  <TableRow
                    key={r.id}
                    hover
                    selected={selectedIds.has(r.id)}
                    onClick={() => toggleRecipient(r.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedIds.has(r.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleRecipient(r.id)}
                      />
                    </TableCell>
                    <TableCell>
                      {`${r.firstName || ''} ${r.lastName || ''}`.trim() || '—'}
                      {channelStatusChip(r.id, sendResults?.email, 'Email')}
                      {channelStatusChip(r.id, sendResults?.sms, 'SMS')}
                    </TableCell>
                    <TableCell>
                      {r.hasEmail ? (
                        r.email
                      ) : (
                        <Chip size="small" label="No email" color="warning" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {r.hasPhone ? (
                        r.phoneNumber
                      ) : (
                        <Chip size="small" label="No phone" color="warning" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="right">{Number(r.distance).toFixed(1)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </>
  );

  const renderHistory = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon fontSize="small" />
          Past sends
        </Typography>
        <Button size="small" onClick={fetchCampaigns} disabled={historyLoading}>
          Refresh
        </Button>
      </Box>

      {historyError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setHistoryError('')}>
          {historyError}
        </Alert>
      )}

      {historyLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={24} />
        </Box>
      ) : campaigns.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          No notifications have been sent yet.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 440 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Area</TableCell>
                <TableCell>Channels</TableCell>
                <TableCell>Results</TableCell>
                <TableCell align="right">View</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((c) => {
                const channelList = (c.channels || '').split(',').filter(Boolean);
                return (
                  <TableRow key={c.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {new Date(c.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <div>{c.area || '—'}</div>
                      {c.radiusMiles != null && (
                        <Typography variant="caption" color="text.secondary">
                          {c.radiusMiles} mi · {c.totalRecipients} in range
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {channelList.map((ch) => (
                        <Chip
                          key={ch}
                          size="small"
                          sx={{ mr: 0.5 }}
                          icon={ch === 'sms' ? <SmsIcon /> : <EmailIcon />}
                          label={ch}
                          variant="outlined"
                        />
                      ))}
                    </TableCell>
                    <TableCell>
                      {channelList.includes('email') && (
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          Email: {c.emailSent} sent
                          {c.emailFailed ? `, ${c.emailFailed} failed` : ''}
                        </Typography>
                      )}
                      {channelList.includes('sms') && (
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          SMS: {c.smsSent} sent{c.smsFailed ? `, ${c.smsFailed} failed` : ''}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" startIcon={<VisibilityIcon />} onClick={() => setPreviewCampaign(c)}>
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  const renderPreviewDialog = () => (
    <Dialog
      open={Boolean(previewCampaign)}
      onClose={() => setPreviewCampaign(null)}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        Message as sent
        {previewCampaign?.subject && (
          <Typography variant="body2" color="text.secondary">
            Subject: {previewCampaign.subject}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {previewCampaign?.emailHtml && (
          <Box sx={{ mb: previewCampaign?.smsBody ? 3 : 0 }}>
            <Typography variant="overline" color="text.secondary">
              Email
            </Typography>
            <Box
              component="iframe"
              title="email-preview"
              srcDoc={previewCampaign.emailHtml}
              sx={{ width: '100%', height: 480, border: '1px solid', borderColor: 'divider', borderRadius: 1, mt: 1 }}
            />
          </Box>
        )}
        {previewCampaign?.smsBody && (
          <Box>
            <Typography variant="overline" color="text.secondary">
              SMS
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mt: 1, whiteSpace: 'pre-wrap', fontSize: 14 }}>
              {previewCampaign.smsBody}
            </Paper>
          </Box>
        )}
        {!previewCampaign?.emailHtml && !previewCampaign?.smsBody && (
          <Typography variant="body2" color="text.secondary">
            No stored content for this send.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPreviewCampaign(null)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CampaignIcon color="primary" />
          <Typography variant="h6" component="span">
            Notify Technicians in Area
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Email and/or text all active technicians within a radius of an area
        </Typography>
        <Tabs value={activeTab} onChange={(_e, v) => setActiveTab(v)} sx={{ mt: 1 }}>
          <Tab label="Compose" />
          <Tab label="History" />
        </Tabs>
      </DialogTitle>

      <DialogContent dividers>
        {activeTab === 0 ? renderCompose() : renderHistory()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Close
        </Button>
      </DialogActions>

      {renderPreviewDialog()}
    </Dialog>
  );
};

export default MaidBroadcastDialog;
